import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import Footer from '@/components/Footer';
import axios from 'axios';
import {
  ArrowRight, User, BookOpen, CheckCircle, GraduationCap,
  ClipboardList, HelpCircle, Lightbulb, BookOpenCheck, MonitorPlay,
  ChevronDown, ChevronUp, MessageSquare, TrendingUp, TrendingDown,
  Target, CalendarDays, BarChart3, Award, Trophy, Clock, XCircle, Zap, Crown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOOL_INFO = {
  'teach-guide': { label: 'دليل التدريس', icon: GraduationCap, color: 'text-cyan-400 bg-cyan-500/20' },
  'tips': { label: 'محتوى الدرس', icon: Lightbulb, color: 'text-amber-400 bg-amber-500/20' },
  'worksheet': { label: 'ورقة عمل', icon: ClipboardList, color: 'text-green-400 bg-green-500/20' },
  'quiz': { label: 'اختبار', icon: HelpCircle, color: 'text-blue-400 bg-blue-500/20' },
  'terms': { label: 'المصطلحات', icon: BookOpen, color: 'text-purple-400 bg-purple-500/20' },
  'explain-lesson': { label: 'شرح الدرس', icon: BookOpenCheck, color: 'text-indigo-400 bg-indigo-500/20' },
  'story': { label: 'القصة', icon: BookOpenCheck, color: 'text-amber-400 bg-amber-500/20' },
  'video': { label: 'الفيديو', icon: MonitorPlay, color: 'text-red-400 bg-red-500/20' },
};
const TOOL_LABELS = { quiz: 'الاختبار', worksheet: 'ورقة العمل' };
const CHAT_CATEGORIES = {
  'طلب إجابة مباشرة': { color: 'bg-red-500', desc: 'يطلب الحل مباشرة' },
  'طلب توضيح': { color: 'bg-blue-500', desc: 'يسأل عن معنى أو شرح' },
  'استكشاف وفضول': { color: 'bg-emerald-500', desc: 'يستكشف ويسأل أسئلة إضافية' },
  'خارج الموضوع': { color: 'bg-orange-500', desc: 'أسئلة غير مرتبطة بالدرس' },
  'محادثة عامة': { color: 'bg-amber-500', desc: 'تحية أو محادثة عامة' },
  'توضيح': { color: 'bg-blue-500', desc: 'طلب توضيح' },
};

export default function AdminStudentProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedScores, setExpandedScores] = useState({});

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/grades'); return; }
    axios.get(`${API_URL}/api/admin/students/${userId}/progress`, { headers: getAuthHeaders() })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, user, navigate, getAuthHeaders]);

  if (!user || user.role !== 'admin') return null;

  const stats = data?.stats || {};

  return (
    <div className="min-h-screen bg-[#0f0f23] text-white" dir="rtl">
      <header className="bg-[#1a1a2e]/80 backdrop-blur-md border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors" onClick={(e) => { e.preventDefault(); navigate(-1); }}>
            <ArrowRight className="w-5 h-5" />
            <span className="text-sm">رجوع</span>
          </Link>
          <h1 className="text-lg font-bold text-white">ملف الطالب</h1>
          <div />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="space-y-6">
            <Skeleton className="h-40 w-full bg-white/5 rounded-2xl" />
            <Skeleton className="h-32 w-full bg-white/5 rounded-2xl" />
          </div>
        ) : !data ? (
          <div className="text-center py-16 text-white/40">لم يتم العثور على بيانات الطالب</div>
        ) : (
          <>
            {/* Profile Card */}
            <div className="mb-6 rounded-2xl bg-gradient-to-l from-[#4da6ff]/10 to-[#1a1a2e] border border-white/10 p-6">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 rounded-full bg-[#4da6ff] flex items-center justify-center shrink-0">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{data.student_name}</h2>
                  <p className="text-white/50 text-sm mt-1">@{data.username}</p>
                </div>
              </div>
            </div>

            {/* Level */}
            {stats.level && (
              <div className="mb-6 rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black ${
                    stats.level.num >= 4 ? 'bg-emerald-500/20 text-emerald-400' : stats.level.num >= 3 ? 'bg-blue-500/20 text-blue-400' : stats.level.num >= 2 ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'
                  }`}>{stats.level.num}</div>
                  <div>
                    <p className={`font-bold text-lg ${stats.level.num >= 4 ? 'text-emerald-400' : stats.level.num >= 3 ? 'text-blue-400' : stats.level.num >= 2 ? 'text-amber-400' : 'text-purple-400'}`}>{stats.level.name}</p>
                    <p className="text-white/30 text-xs">{stats.level.xp} نقطة خبرة</p>
                  </div>
                </div>
                {stats.level.next_xp && (
                  <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${stats.level.num >= 3 ? 'bg-blue-500' : stats.level.num >= 2 ? 'bg-amber-500' : 'bg-purple-500'}`} style={{ width: `${Math.min(100, Math.round((stats.level.xp / stats.level.next_xp) * 100))}%` }} />
                  </div>
                )}
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <StatCard icon={<BookOpen className="w-5 h-5" />} value={data.total_lessons_touched} label="دروس" color="text-[#4da6ff]" />
              <StatCard icon={<CheckCircle className="w-5 h-5" />} value={data.total_tools_completed} label="أنشطة" color="text-emerald-400" />
              <StatCard icon={<Target className="w-5 h-5" />} value={stats.total_attempts || 0} label="اختبارات" color="text-amber-400" />
              <StatCard icon={<CalendarDays className="w-5 h-5" />} value={stats.active_days || 0} label="أيام نشاط" color="text-purple-400" />
            </div>

            {/* Performance */}
            {stats.avg_score_pct > 0 && (
              <div className="mb-6 rounded-2xl bg-white/5 border border-white/10 p-5">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#4da6ff]" />الأداء</h3>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white/50 text-sm">المستوى الحالي</span>
                  <span className={`text-lg font-bold ${stats.avg_score_pct >= 80 ? 'text-emerald-400' : stats.avg_score_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{stats.avg_score_pct}%</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${stats.avg_score_pct >= 80 ? 'bg-emerald-500' : stats.avg_score_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${stats.avg_score_pct}%` }} />
                </div>
              </div>
            )}

            {/* Chat Analysis */}
            {stats.chat_analysis && (
              <div className="mb-6 rounded-2xl bg-white/5 border border-white/10 p-5">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-indigo-400" />تحليل المحادثات</h3>
                <div className="flex gap-4 mb-3 text-xs text-white/40">
                  <span>{stats.chat_analysis.total_messages} رسالة</span>
                  <span>{stats.chat_analysis.lessons_chatted} درس</span>
                </div>
                <div className="space-y-2">
                  {stats.chat_analysis.categories.map(cat => {
                    const info = CHAT_CATEGORIES[cat.name] || { color: 'bg-gray-500', desc: '' };
                    return (
                      <div key={cat.name}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${info.color}`} />
                            <span className="text-white/70 text-xs">{cat.name}</span>
                          </div>
                          <span className="text-white/50 text-xs">{cat.count} ({cat.pct}%)</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${info.color}`} style={{ width: `${cat.pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Olympics Stats */}
            {stats.olympics && stats.olympics.subjects?.length > 0 && (
              <div className="mb-6 rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="admin-olympics-stats">
                <h3 className="text-sm font-bold text-amber-400 mb-4 flex items-center gap-2">
                  <Trophy className="w-4 h-4" /> أولمبياد رشيد جي بي تي
                </h3>

                {/* Olympics summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  <div className="text-center bg-emerald-500/10 rounded-xl p-3 border border-emerald-500/20">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-emerald-400">{stats.olympics.total_correct}</p>
                    <p className="text-[10px] text-white/40">إجابة صحيحة</p>
                  </div>
                  <div className="text-center bg-red-500/10 rounded-xl p-3 border border-red-500/20">
                    <XCircle className="w-4 h-4 text-red-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-red-400">{stats.olympics.total_wrong}</p>
                    <p className="text-[10px] text-white/40">إجابة خاطئة</p>
                  </div>
                  <div className="text-center bg-[#4da6ff]/10 rounded-xl p-3 border border-[#4da6ff]/20">
                    <Zap className="w-4 h-4 text-[#4da6ff] mx-auto mb-1" />
                    <p className="text-xl font-bold text-[#4da6ff]">{stats.olympics.total_attempts}</p>
                    <p className="text-[10px] text-white/40">إجمالي المحاولات</p>
                  </div>
                  <div className="text-center bg-purple-500/10 rounded-xl p-3 border border-purple-500/20">
                    <Clock className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-purple-400">{stats.olympics.total_time}ث</p>
                    <p className="text-[10px] text-white/40">إجمالي الوقت</p>
                  </div>
                </div>

                {/* Per-subject breakdown */}
                <div className="space-y-3">
                  {stats.olympics.subjects.map(s => {
                    const pct = s.pct;
                    const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
                    const textColor = pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400';
                    return (
                      <div key={s.subject_id} className="rounded-xl bg-white/5 border border-white/10 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-white text-sm font-medium">{s.name_ar}</p>
                            <p className="text-white/30 text-[10px]">{s.grade_name}</p>
                          </div>
                          {s.rank > 0 && (
                            <span className="flex items-center gap-1 text-amber-400 text-xs bg-amber-500/10 px-2 py-1 rounded-lg border border-amber-500/20">
                              <Crown className="w-3 h-3" />
                              {s.rank} من {s.total_grade_students}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-2 mb-2 text-center">
                          <div>
                            <p className="text-emerald-400 text-sm font-bold">{s.correct}</p>
                            <p className="text-white/30 text-[9px]">صحيحة</p>
                          </div>
                          <div>
                            <p className="text-red-400 text-sm font-bold">{s.wrong}</p>
                            <p className="text-white/30 text-[9px]">خاطئة</p>
                          </div>
                          <div>
                            <p className="text-[#4da6ff] text-sm font-bold">{s.attempts}</p>
                            <p className="text-white/30 text-[9px]">محاولات</p>
                          </div>
                          <div>
                            <p className="text-purple-400 text-sm font-bold">{s.total_time}ث</p>
                            <p className="text-white/30 text-[9px]">الوقت</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className={`text-xs font-bold ${textColor} w-10 text-left`}>{pct}%</span>
                        </div>
                        <p className="text-white/30 text-[10px] mt-1">{s.correct} من {s.total_questions} سؤال</p>
                      </div>
                    );
                  })}
                </div>

                {/* Parent feedback */}
                <div className="mt-4 rounded-xl p-3 bg-amber-500/5 border border-amber-500/15">
                  <div className="flex items-center gap-1.5 mb-1.5 text-amber-400">
                    <Lightbulb className="w-3.5 h-3.5" />
                    <span className="font-semibold text-xs">ملاحظة لأولياء الأمور</span>
                  </div>
                  <p className="text-white/50 text-xs leading-relaxed">
                    {stats.olympics.total_correct === 0
                      ? 'لم يبدأ الطالب بعد في أولمبياد رشيد جي بي تي. شجّعوه على خوض التحدي واختبار معلوماته!'
                      : stats.olympics.total_correct > 0 && stats.olympics.total_wrong > stats.olympics.total_correct
                        ? `الطالب يحاول ولكن نسبة الإجابات الخاطئة مرتفعة. ننصح بمراجعة الدروس قبل الإجابة على الأسئلة والتركيز على الفهم أولاً.`
                        : stats.olympics.total_correct > 20
                          ? `أداء ممتاز! الطالب أجاب على ${stats.olympics.total_correct} سؤال بشكل صحيح. شجّعوه على الاستمرار والتنافس مع زملائه.`
                          : `بداية جيدة! الطالب أجاب على ${stats.olympics.total_correct} سؤال بشكل صحيح. تابعوا تشجيعه على حل المزيد من الأسئلة.`
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Lessons Progress */}
            <h3 className="text-sm font-bold text-white mb-3">تقدم الدروس</h3>
            {data.lessons?.length > 0 ? (
              <div className="space-y-3">
                {data.lessons.map(lesson => (
                  <Card key={lesson.lesson_id} className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="text-white text-sm font-medium">{lesson.title}</p>
                          <p className="text-white/30 text-xs">{lesson.grade_name} {lesson.subject_name ? `- ${lesson.subject_name}` : ''}</p>
                        </div>
                        <span className="flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 rounded-lg px-2 py-0.5 shrink-0">
                          <CheckCircle className="w-3 h-3 text-emerald-400" />
                          <span className="text-emerald-400 text-xs font-bold">{lesson.completed_tools.length}</span>
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {lesson.completed_tools.map(tool => {
                          const info = TOOL_INFO[tool] || { label: tool, icon: CheckCircle, color: 'text-white/60 bg-white/10' };
                          const Icon = info.icon;
                          return <span key={tool} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${info.color}`}><Icon className="w-2.5 h-2.5" />{info.label}</span>;
                        })}
                      </div>
                      {lesson.scores?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5">
                          {lesson.scores.map((s, i) => {
                            const pct = s.total > 0 ? Math.round((s.score / s.total) * 100) : 0;
                            const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
                            const textColor = pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400';
                            const scoreKey = `${lesson.lesson_id}-${i}`;
                            const isExpanded = expandedScores[scoreKey];
                            return (
                              <div key={i}>
                                <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpandedScores(p => ({ ...p, [scoreKey]: !p[scoreKey] }))}>
                                  <span className="text-white/40 text-xs w-24 shrink-0">{TOOL_LABELS[s.tool_type] || s.tool_type}{s.attempt_num > 1 && ` (م${s.attempt_num})`}</span>
                                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                                  </div>
                                  <span className={`text-xs font-bold ${textColor} w-16 text-left`} dir="ltr">{s.score}/{s.total} ({pct}%)</span>
                                  {s.feedback && (isExpanded ? <ChevronUp className="w-3 h-3 text-white/20" /> : <ChevronDown className="w-3 h-3 text-white/20" />)}
                                </div>
                                {isExpanded && s.feedback && (
                                  <div className="mr-2 mt-1 p-2 rounded bg-[#4da6ff]/5 border border-[#4da6ff]/10 text-[11px] text-white/60 leading-relaxed whitespace-pre-wrap">{s.feedback}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-white/30 rounded-2xl bg-white/5">لم يبدأ أي درس بعد</div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

const StatCard = ({ icon, value, label, color }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-center">
    <div className={`flex justify-center mb-1 ${color}`}>{icon}</div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    <p className="text-white/30 text-[10px]">{label}</p>
  </div>
);
